/**
 * @module core.liftr
 * @version 4.6.0
 * @description Unified LIFTR Core - Constants, Debug, Events, I18N, Settings
 * 
 * Migration von v3.9:
 * - Vereint: core.const, core.debug, core.events, core.i18n, core.settings
 * - Entfernt: Boot-System, Status-Lifecycle, Type-Unterscheidung
 * - Events: liftr:{name}:{action} statt liftr:{type}:{name}:{action}
 * 
 * Changelog:
 * - 4.6.0: Added stock thresholds to market settings
 *        - market.stock.threshold_warning (default: 20)
 *        - market.stock.threshold_critical (default: 5)
 * - 4.5.0: Added FX.PROPERTIES for CSS custom property constants
 *        - Centralizes --fx-* variable names
 *        - Prevents string literals in modules
 * - 4.4.0: NAV refactor - clear auto/static separation
 *        - auto: liftr-nav on root only
 *        - static: liftr-nav on buttons only, liftr-target mandatory
 *        - Removed applySettingsToButton() - static buttons self-contained
 * - 4.3.0: Added NAV.applySettingsToButton() for external buttons via liftr-target
 * - 4.2.0: Added NAV.applyOutput(), NAV.createButton(), NAV.ensureRoles() helpers
 * - 4.1.1: Added shipping.minimum to validateMarket() for delivery pricing
 * - 4.1.0: Added ATTRIBUTES.PERSISTENT for filter exclusion
 * - 4.0.0: Initial v4.0 implementation
 * 
 * @example
 * import { debug, emit, on, i18n, settings, ATTRIBUTES, NAV, FX } from './core.liftr.js';
 */

/* ═══════════════════════════════════════════════════════════════════
 * CONSTANTS
 * ═══════════════════════════════════════════════════════════════════ */

/** Framework Log-Handle für Console-Ausgaben */
export const LOG_HANDLE = '[liftr]';

/**
 * DOM-Attribute für Module (v4.0 - vereinfacht)
 * Reduziert von 15+ auf 10 Attribute
 */
export const ATTRIBUTES = Object.freeze({
  // Identity
  ID:       'liftr-id',       // Human-readable identifier + target reference
  ROLE:     'liftr-role',     // Child element roles (space-separated)
  TARGET:   'liftr-target',   // Target reference for shared controls
  
  // Input (set in Liquid)
  AXIS:     'liftr-axis',     // Scroll/slide axis: x, y, xy
  MASK:     'liftr-mask',     // Overflow fade masks: left, right, x, y, all
  
  // Output (set by JS)
  NAV:      'liftr-nav',      // Navigation settings (auto: on root, static: on buttons)
  STATE:    'liftr-state',    // State tokens: active, disabled, can-prev, can-next
  SETTINGS: 'liftr-settings', // Output for CSS Styles
    
  // Dependencies
  REQUIRES: 'liftr-requires',  // Dependency declaration: postal, geolocation, etc.

  // Data (for list filtering)
  FIELD:    'liftr-field',    // Field name for filtering/sorting
  VALUE:    'liftr-value',    // Field value (space-separated for multi-match)
  MERGE:    'liftr-merge',    
  
  // Notifications & filter
  PERSIST: 'liftr-persist'    // Exclude from refresh and auto dismiss
});

/**
 * Effect class constants (ui.effects.css).
 * Single source of truth for fx-* class names and --fx-* CSS properties.
 * @type {Readonly<Object>}
 */
export const FX = Object.freeze({
  // ═══════════════════════════════════════════════════════════════════
  // CLASSES (for classList.add/remove)
  // ═══════════════════════════════════════════════════════════════════
  
  // TRANSITIONS (Snapshot Pattern)
  MORPH:            'fx-morph',
  
  // ENTER/LEAVE (One-shot Animations)
  ENTER_POP:       'fx-enter-pop',
  EXIT_POP:        'fx-exit-pop',
  ENTER_FADE:      'fx-enter-fade',
  EXIT_FADE:       'fx-exit-fade',
  
  // LOADING STATES
  LOADING:          'fx-loading',
  
  // HOVER EFFECTS
  HOVER_PRIMARY:    'fx-hover-primary',
  HOVER_SECONDARY:  'fx-hover-secondary',
  HOVER_MUTED:      'fx-hover-muted',
  NONE:             'fx-none',
  
  // AMBIENT (Continuous Background)
  AMBIENT_GLOW:     'fx-ambient-glow',
  AMBIENT_PULSE:    'fx-ambient-pulse',
  AMBIENT_SHIMMER:  'fx-ambient-shimmer',
  
  // ATTENTION
  ATTENTION_PULSE:  'fx-attention-pulse',
  
  // ═══════════════════════════════════════════════════════════════════
  // PROPERTIES (CSS Custom Properties read by JavaScript)
  // ═══════════════════════════════════════════════════════════════════
  
  /**
   * CSS Custom Properties for FX Set timing.
   * JavaScript reads these via getComputedStyle().
   * Defined per-set in ui.effects.css.
   * 
   * @example
   * const duration = getComputedStyle(el).getPropertyValue(FX.PROPERTIES.MORPH_DURATION);
   */

  PROPERTIES: Object.freeze({
    // HOVER
    HOVER_DURATION:       '--fx-hover-duration',
    HOVER_DURATION_LONG:  '--fx-hover-duration-long',
    HOVER_EASE:           '--fx-hover-ease',
    HOVER_FILL:           '--fx-hover-fill',
    
    // LOADING
    LOADING_EXIT_DURATION:    '--fx-loading-exit-duration',
    LOADING_EXIT_EASE:        '--fx-loading-exit-ease',
    LOADING_RESULT_DURATION:  '--fx-loading-result-duration',

    // ENTER/LEAVE (was POP)
    ENTER_DURATION:   '--fx-enter-duration',
    ENTER_EASE:       '--fx-enter-ease',
    EXIT_DURATION:    '--fx-exit-duration',
    EXIT_EASE:        '--fx-exit-ease',
    
    // MORPH (unchanged)
    MORPH_DURATION:   '--fx-morph-duration',
    MORPH_EASE:       '--fx-morph-ease',

    // AMBIENT (unchanged)
    AMBIENT_DURATION:   '--fx-ambient-duration',
    AMBIENT_EASE:       '--fx-ambient-ease',
    AMBIENT_INTENSITY:  '--fx-ambient-intensity',

    // NOTIFICATION
    NOTIFICATION_ENTER_DURATION:  '--fx-notification-enter-duration',
    NOTIFICATION_ENTER_EASE:      '--fx-notification-enter-ease',
    NOTIFICATION_EXIT_DURATION:   '--fx-notification-exit-duration',
    NOTIFICATION_EXIT_EASE:       '--fx-notification-exit-ease',
    NOTIFICATION_STAGGER:         '--fx-notification-stagger',
  })

});

/**
 * Event-System Konstanten (v4.0)
 * Pattern geändert: liftr:{name}:{action} (kein type-Segment mehr)
 */
export const EVENT = Object.freeze({
  // Namenskonvention Pattern: liftr:{module}:{action}
  PATTERN: /^liftr:[a-z0-9-]+:[a-z0-9-]+$/,
});

/** Settings & I18N Script-IDs */
export const SCRIPT_IDS = Object.freeze({
  SETTINGS: 'liftr-settings',
  I18N:     'liftr-i18n'
});

/* ═══════════════════════════════════════════════════════════════════
 * NAV - Navigation Control System
 * ═══════════════════════════════════════════════════════════════════ */

/**
 * Navigation control constants and helpers
 * 
 * Two modes with clear separation:
 * - auto: Module generates buttons, liftr-nav on ROOT
 * - static: Buttons in DOM with liftr-target, liftr-nav on BUTTONS
 * 
 * @example
 * // AUTO MODE: Apply nav output to root
 * NAV.applyOutput(this, navSettings);
 * const btn = NAV.createButton('slider-prev', 'prev', 'Previous');
 * 
 * // STATIC MODE: Find external buttons (self-contained, no root attribute)
 * const prevBtn = this.target('slider-prev');
 * NAV.ensureRoles(prevBtn, 'prev');
 */
export const NAV = Object.freeze({
  // CSS Settings defaults
  DEFAULTS: Object.freeze({
    MODE:     'auto',
    SIZE:     'regular',
    ICON:     'arrow',
    THEME:    'light',
    STYLE:    'round',
    POSITION: 'inside'
  }),
  
  // Valid values for validation
  VALID: Object.freeze({
    MODE:     ['false', 'auto', 'static'],
    SIZE:     ['small', 'regular', 'medium', 'large'],
    ICON:     ['angle', 'arrow', 'chevron'],
    THEME:    ['light', 'dark', 'plain'],
    STYLE:    ['square', 'round'],
    POSITION: ['inside', 'outside', 'edge']
  }),
  
  // Direction to shared role mapping
  DIRECTION_ROLES: Object.freeze({
    prev:   'nav-prev',
    next:   'nav-next',
    up:     'nav-up',
    down:   'nav-down',
    toggle: 'nav-toggle'
  }),
  
  // Shared role name
  CONTROL_ROLE: 'nav-control',
  
  /**
   * Apply navigation output attribute to ROOT element (auto mode only).
   * 
   * @param {HTMLElement} root - Module root element
   * @param {object} settings - Settings from watchSettings({ nav: true })
   * 
   * @example
   * NAV.applyOutput(this, this.#navSettings);
   * // Result: <liftr-slider liftr-nav="medium angle light round inside">
   */
  applyOutput(root, settings) {
    if (!root || !settings) return;
    
    // Static/false: no attribute on root
    if (settings.mode === 'static' || settings.mode === 'false') {
      root.removeAttribute(ATTRIBUTES.NAV);
      return;
    }
    
    // Auto mode: full settings on root
    const value = [
      settings.size,
      settings.icon,
      settings.theme,
      settings.theme !== 'plain' ? settings.style : null,
      settings.position
    ].filter(Boolean).join(' ');
    
    root.setAttribute(ATTRIBUTES.NAV, value);
  },
  
  /**
   * Apply navigation output attribute to BUTTON element (static mode).
   * 
   * @param {HTMLElement} button - Button element
   * @param {object} settings - Settings from watchSettings({ nav: true })
   * 
   * @example
   * NAV.applyToButton(prevBtn, this.#navSettings);
   * // Result: <button liftr-nav="medium angle light round">
   */
  applyToButton(button, settings) {
    if (!button || !settings) return;
    
    // Visual settings only (no mode, no position)
    const value = [
      settings.size,
      settings.icon,
      settings.theme,
      settings.theme !== 'plain' ? settings.style : null
    ].filter(Boolean).join(' ');
    
    button.setAttribute(ATTRIBUTES.NAV, value);
  },
  
  /**
   * Create a navigation button element (auto mode only).
   * 
   * @param {string} moduleRole - Module-specific role (e.g., 'slider-prev')
   * @param {string} direction - Direction: prev|next|up|down|toggle
   * @param {string} [label] - Accessible label
   * @returns {HTMLButtonElement}
   * 
   * @example
   * const prevBtn = NAV.createButton('slider-prev', 'prev', labels.prev);
   * // Result: <button type="button" liftr-role="slider-prev nav-control nav-prev" aria-label="...">
   */
  createButton(moduleRole, direction, label) {
    const btn = document.createElement('button');
    btn.type = 'button';
    
    if (label) {
      btn.setAttribute('aria-label', label);
    }
    
    const dirRole = NAV.DIRECTION_ROLES[direction] || `nav-${direction}`;
    const roles = [moduleRole, NAV.CONTROL_ROLE, dirRole].filter(Boolean).join(' ');
    btn.setAttribute(ATTRIBUTES.ROLE, roles);
    
    return btn;
  },
  
  /**
   * Add shared nav roles to existing button (static mode).
   * Static buttons already have liftr-nav set via Liquid/CSS.
   * 
   * @param {HTMLElement} button - Existing button element
   * @param {string} direction - Direction: prev|next|up|down|toggle
   * 
   * @example
   * // For static mode buttons that need shared roles added
   * const prevBtn = this.target('slider-prev');
   * NAV.ensureRoles(prevBtn, 'prev');
   */
  ensureRoles(button, direction) {
    if (!button) return;
    
    const dirRole = NAV.DIRECTION_ROLES[direction] || `nav-${direction}`;
    const existing = button.getAttribute(ATTRIBUTES.ROLE) || '';
    const roleSet = new Set(existing.split(/\s+/).filter(Boolean));
    
    roleSet.add(NAV.CONTROL_ROLE);
    roleSet.add(dirRole);
    
    button.setAttribute(ATTRIBUTES.ROLE, [...roleSet].join(' '));
  }
});

/* ═══════════════════════════════════════════════════════════════════
 * DEBUG
 * ═══════════════════════════════════════════════════════════════════ */

/**
 * Debug Configuration (wird von Settings überschrieben)
 * @private
 */
const DEBUG_CONFIG = {
  enabled: true,
  verbose: false
};

/**
 * DRY helper for console logging
 * @private
 */
function log(method, prefix, message, data) {
  const msg = `${LOG_HANDLE} ${prefix}${message}`;
  if (data !== undefined) {
    console[method](msg, data);
  } else {
    console[method](msg);
  }
}

/**
 * Debug-System mit 3+1 Log-Levels
 * 
 * LEVELS:
 * - info()    → Wichtige Events - requires enabled
 * - warn()    → Warnings - requires enabled
 * - error()   → Kritische Fehler - IMMER sichtbar
 * - verbose() → Deep debugging - requires enabled + verbose
 */
export const debug = {
  /** @type {boolean} Debug enabled (false = nur errors) */
  get enabled() { return DEBUG_CONFIG.enabled; },
  set enabled(v) { DEBUG_CONFIG.enabled = !!v; },
  
  /** @type {boolean} Verbose mode (true = deep debugging) */
  get verbose() { return DEBUG_CONFIG.verbose; },
  set verbose(v) { DEBUG_CONFIG.verbose = !!v; },
  
  /**
   * Info - Wichtige Lifecycle Events
   * @param {string} message
   * @param {*} [data]
   */
  info(message, data) {
    if (!DEBUG_CONFIG.enabled) return;
    log('log', '', message, data);
  },
  
  /**
   * Warn - Non-critical Issues
   * @param {string} message
   * @param {*} [data]
   */
  warn(message, data) {
    if (!DEBUG_CONFIG.enabled) return;
    log('warn', '⚠️ ', message, data);
  },
  
  /**
   * Error - Kritische Fehler (IMMER sichtbar)
   * @param {string} message
   * @param {*} [data]
   */
  error(message, data) {
    log('error', '❌ ', message, data);
  },
  
  /**
   * Verbose - Deep Debugging
   * @param {string} message
   * @param {*} [data]
   */
  verbose(message, data) {
    if (!DEBUG_CONFIG.enabled || !DEBUG_CONFIG.verbose) return;
    const timestamp = new Date().toISOString().split('T')[1].split('.')[0];
    log('log', `[${timestamp}] `, message, data);
  },
  
  /**
   * Event-Log (verbose only)
   * @param {string} eventName
   * @param {*} detail
   * @param {string} [direction='→']
   */
  event(eventName, detail, direction = '→') {
    if (!DEBUG_CONFIG.enabled || !DEBUG_CONFIG.verbose) return;
    log('log', `[event ${direction}] `, eventName, detail);
  },
  
  /**
   * Gruppierte Logs (verbose only)
   * @param {string} title
   * @param {Function} [callback]
   */
  group(title, callback) {
    if (!DEBUG_CONFIG.enabled || !DEBUG_CONFIG.verbose) return;
    console.group(`${LOG_HANDLE} ${title}`);
    if (typeof callback === 'function') callback();
    console.groupEnd();
  },
  
  /**
   * Performance-Messung starten (verbose only)
   * @param {string} label
   */
  timeStart(label) {
    if (!DEBUG_CONFIG.enabled || !DEBUG_CONFIG.verbose) return;
    console.time(`${LOG_HANDLE} ⏱️ ${label}`);
  },
  
  /**
   * Performance-Messung beenden (verbose only)
   * @param {string} label
   */
  timeEnd(label) {
    if (!DEBUG_CONFIG.enabled || !DEBUG_CONFIG.verbose) return;
    console.timeEnd(`${LOG_HANDLE} ⏱️ ${label}`);
  }
};

/* ═══════════════════════════════════════════════════════════════════
 * EVENTS
 * ═══════════════════════════════════════════════════════════════════ */

/**
 * Registry für RegEx-Pattern Listener
 * @private
 */
const regexListeners = [];

/**
 * Prüft ob Event-Name der Konvention entspricht
 * Pattern: liftr:{module}:{action}
 * 
 * @param {string} name - Event-Name
 * @returns {boolean} true wenn gültig
 * 
 * @example
 * isValidEventName('liftr:accordion:opened')  // true
 * isValidEventName('liftr:slider:slide-changed')  // true
 * isValidEventName('click')  // false
 */
export function isValidEventName(name) {
  try {
    return EVENT.PATTERN.test(String(name));
  } catch {
    return false;
  }
}

/**
 * Dispatched ein Custom Event
 * 
 * @param {string} name - Event-Name (liftr:{module}:{action})
 * @param {object} [detail={}] - Event-Detail/Payload
 * @param {object} [options={}] - Event-Optionen
 * @param {EventTarget} [options.target=document] - Event-Target
 * @param {boolean} [options.bubbles=true] - Soll Event bubblen
 * @param {boolean} [options.composed=true] - Soll Event Shadow-DOM durchqueren
 * @param {boolean} [options.cancelable=false] - Kann Event gecancelt werden
 * @returns {boolean} true wenn nicht verhindert (defaultPrevented === false)
 * 
 * @example
 * emit('liftr:accordion:opened', { index: 0, item: element });
 * emit('liftr:slider:slide-changed', { from: 0, to: 1 });
 */
export function emit(name, detail = {}, options = {}) {
  if (!isValidEventName(name)) {
    debug.warn(`Ungültiger Event-Name (erwartet liftr:{module}:{action}): ${name}`);
  }
  
  const target = options.target || document;
  
  const event = new CustomEvent(name, {
    detail,
    bubbles: options.bubbles !== false,
    composed: options.composed !== false,
    cancelable: !!options.cancelable
  });
  
  debug.event(name, detail, 'emit');
  
  // Native dispatch
  const result = target.dispatchEvent(event);
  
  // RegEx listeners (snapshot to prevent mutation during iteration)
  regexListeners.slice().forEach(({ pattern, handler, options: listenerOptions }) => {
    if (pattern.test(name)) {
      const matchTarget = listenerOptions?.target || document;
      
      if (matchTarget === target || matchTarget === document) {
        try {
          handler(event);
        } catch (error) {
          debug.error('RegEx event handler failed:', error);
        }
      }
    }
  });
  
  return result;
}

/**
 * Registriert Event-Listener mit Auto-Cleanup
 * 
 * @param {string|RegExp} name - Event-Name oder RegEx Pattern
 * @param {Function} handler - Event-Handler
 * @param {object} [options={}] - Listener-Optionen
 * @param {EventTarget} [options.target=document] - Event-Target
 * @param {AddEventListenerOptions} [options.listenerOptions] - Native addEventListener Optionen
 * @param {AbortSignal} [options.signal] - AbortSignal für Cleanup
 * @returns {Function} Cleanup-Funktion zum Entfernen des Listeners
 * 
 * @example
 * // String-based
 * const cleanup = on('liftr:accordion:opened', (e) => console.log(e.detail));
 * cleanup(); // Remove listener
 * 
 * // RegEx-based (listen to all accordion events)
 * on(/^liftr:accordion:/, handler);
 */
export function on(name, handler, options = {}) {
  // RegEx Support
  if (name instanceof RegExp) {
    const listener = { 
      pattern: name, 
      handler, 
      options: options || {}
    };
    
    regexListeners.push(listener);
    debug.verbose('RegEx event listener registered:', name.source);
    
    return () => {
      const index = regexListeners.findIndex(l => l.handler === handler && l.pattern === name);
      if (index > -1) {
        regexListeners.splice(index, 1);
        debug.verbose('RegEx event listener removed:', name.source);
      }
    };
  }
  
  // String-based
  const target = options.target || document;
  const listenerOptions = options.listenerOptions || {};
  
  let controller;
  if (options.signal) {
    listenerOptions.signal = options.signal;
  } else {
    controller = new AbortController();
    listenerOptions.signal = controller.signal;
  }
  
  const wrappedHandler = (event) => {
    debug.event(name, event.detail, 'listen');
    handler(event);
  };
  
  target.addEventListener(name, wrappedHandler, listenerOptions);
  
  return () => {
    if (controller) {
      controller.abort();
    }
  };
}

/**
 * Registriert One-Time Event-Listener
 * 
 * @param {string|RegExp} name - Event-Name oder RegEx Pattern
 * @param {Function} handler - Event-Handler
 * @param {object} [options={}] - Listener-Optionen
 * @returns {Function} Cleanup-Funktion
 */
export function once(name, handler, options = {}) {
  if (name instanceof RegExp) {
    let cleanup;
    const onceHandler = (event) => {
      handler(event);
      if (cleanup) cleanup();
    };
    cleanup = on(name, onceHandler, options);
    return cleanup;
  }
  
  const listenerOptions = options.listenerOptions || {};
  listenerOptions.once = true;
  
  return on(name, handler, {
    ...options,
    listenerOptions
  });
}

/* ═══════════════════════════════════════════════════════════════════
 * I18N
 * ═══════════════════════════════════════════════════════════════════ */

/**
 * I18N Daten (lazy loaded)
 * @private
 */
let i18nData = null;

/**
 * Lädt und parst i18n-Daten aus DOM
 * @private
 * @returns {{ locale: string, translations: object }}
 */
function loadI18nFromDOM() {
  const script = document.getElementById(SCRIPT_IDS.I18N);
  
  if (!script) {
    debug.warn(`i18n-Script nicht gefunden (ID: ${SCRIPT_IDS.I18N})`);
    return { locale: 'de', translations: {} };
  }

  const locale = script.dataset.locale || 'de';

  try {
    const translations = JSON.parse(script.textContent);
    return { locale, translations };
  } catch (error) {
    debug.error(`i18n-JSON ungültig: ${error.message}`);
    return { locale, translations: {} };
  }
}

/**
 * Ensures i18n data is loaded
 * @private
 */
function ensureI18n() {
  if (!i18nData) {
    i18nData = loadI18nFromDOM();
  }
  return i18nData;
}

/**
 * Holt eine Übersetzung via Pfad
 * @private
 * @param {string} path - Punkt-separierter Pfad
 * @param {object} [vars] - Template-Variablen
 * @returns {string|null}
 */
function translate(path, vars = {}) {
  const data = ensureI18n();
  const keys = path.split('.');
  let value = data.translations;

  for (const key of keys) {
    if (value && typeof value === 'object' && key in value) {
      value = value[key];
    } else {
      debug.verbose(`i18n key nicht gefunden: ${path}`);
      return null;
    }
  }

  if (typeof value === 'string' && Object.keys(vars).length > 0) {
    return value.replace(/\{(\w+)\}/g, (match, key) => {
      return vars[key] !== undefined ? vars[key] : match;
    });
  }

  return value;
}

/**
 * Holt alle Übersetzungen für ein Modul
 * @private
 * @param {string} moduleName - Name des Moduls
 * @returns {object|null}
 */
function getModuleTranslations(moduleName) {
  const data = ensureI18n();
  return data.translations?.modules?.[moduleName] || null;
}

/**
 * I18N API
 * 
 * @example
 * i18n.locale // 'de'
 * i18n.t('global.loading') // 'Laden...'
 * i18n.t('global.items', { count: 5 }) // '5 Artikel'
 * i18n.get('accordion') // { toggle: 'Öffnen/Schließen', ... }
 */
export const i18n = {
  /** Current locale */
  get locale() { return ensureI18n().locale; },
  
  /** 
   * Translate by path
   * @param {string} path - Dot-separated path
   * @param {object} [vars] - Template variables
   */
  t: translate,
  
  /**
   * Get all translations for a module
   * @param {string} name - Module name
   */
  get: getModuleTranslations,
  
  /** Global/shared translations */
  get global() { return ensureI18n().translations?.global || null; }
};

/* ═══════════════════════════════════════════════════════════════════
 * SETTINGS
 * ═══════════════════════════════════════════════════════════════════ */

/**
 * Settings Daten (lazy loaded)
 * @private
 */
let settingsData = null;

/**
 * Single source of truth for market defaults
 * @private
 */
const MARKET_DEFAULTS = {
  handle: null,
  money: '{{amount_with_comma_separator}} €',
  timezone: 'Europe/Berlin',
  holidays: null,
  opening: {
    schedule: { regular: null, holiday: null }
  },
  shipping: {
    transit_time: null,
    minimum: null,
    schedule: { regular: null, holiday: null }
  },
  stock: {
    threshold_warning: 20,
    threshold_critical: 5
  },
  rewards: []
};

/**
 * Normalisiert Reward-Daten aus Metaobject-Format
 * @private
 */
function normalizeRewards(rawRewards) {
  if (!Array.isArray(rawRewards)) return [];
  
  return rawRewards
    .map(r => ({
      name: r.name || '',
      threshold: Math.round(parseFloat(r.threshold?.amount || 0) * 100),
      type: r.type || '',
      free_product_id: r.free_product 
        ? parseInt(r.free_product.split('/').pop(), 10) 
        : null,
      discount_percent: r.discount_percent || null,
      discount_value: r.discount_value?.amount 
        ? Math.round(parseFloat(r.discount_value.amount) * 100) 
        : null
    }))
    .filter(r => r.threshold > 0)
    .sort((a, b) => a.threshold - b.threshold);
}

/**
 * Normalisiert Shipping Minimum aus Metafield
 * @private
 * @param {Object} minimum - { amount: "3.95", currency_code: "EUR" }
 * @returns {Object|null} - { amount: 395, currency_code: "EUR" } or null
 */
function normalizeShippingMinimum(minimum) {
  if (!minimum?.amount) return null;
  
  return {
    amount: Math.round(parseFloat(minimum.amount) * 100),
    currency_code: minimum.currency_code || 'EUR'
  };
}

/**
 * Validiert Market-Daten
 * @private
 */
function validateMarket(market) {
  if (!market || typeof market !== 'object') {
    return { ...MARKET_DEFAULTS };
  }

  return {
    handle: market.handle || MARKET_DEFAULTS.handle,
    money: market.money || MARKET_DEFAULTS.money,
    timezone: market.timezone || MARKET_DEFAULTS.timezone,
    holidays: Array.isArray(market.holidays) ? market.holidays : MARKET_DEFAULTS.holidays,
    opening: {
      schedule: {
        regular: market.opening?.schedule?.regular || MARKET_DEFAULTS.opening.schedule.regular,
        holiday: market.opening?.schedule?.holiday || MARKET_DEFAULTS.opening.schedule.holiday
      }
    },
    shipping: {
      transit_time: market.shipping?.transit_time || MARKET_DEFAULTS.shipping.transit_time,
      minimum: normalizeShippingMinimum(market.shipping?.minimum),
      schedule: {
        regular: market.shipping?.schedule?.regular || MARKET_DEFAULTS.shipping.schedule.regular,
        holiday: market.shipping?.schedule?.holiday || MARKET_DEFAULTS.shipping.schedule.holiday
      }
    },
    stock: {
      threshold_warning: market.stock?.threshold_warning ?? MARKET_DEFAULTS.stock.threshold_warning,
      threshold_critical: market.stock?.threshold_critical ?? MARKET_DEFAULTS.stock.threshold_critical
    },
    rewards: normalizeRewards(market.rewards)
  };
}

/**
 * Lädt Settings aus DOM
 * @private
 */
function loadSettingsFromDOM() {
  const script = document.getElementById(SCRIPT_IDS.SETTINGS);
  
  if (!script) {
    debug.warn(`Settings-Script nicht gefunden (ID: ${SCRIPT_IDS.SETTINGS})`);
    return {};
  }

  try {
    return JSON.parse(script.textContent);
  } catch (error) {
    debug.error(`Settings-JSON ungültig: ${error.message}`);
    return {};
  }
}

/**
 * Initialisiert Settings-System
 * @private
 */
function initSettings() {
  const raw = loadSettingsFromDOM();

  // Debug-Config aus Settings übernehmen
  const debugConfig = {
    enabled: raw.debug?.enabled ?? true,
    verbose: raw.debug?.verbose ?? false
  };
  
  // Debug-System konfigurieren
  DEBUG_CONFIG.enabled = debugConfig.enabled;
  DEBUG_CONFIG.verbose = debugConfig.verbose;

  // Modul-Settings vereinen (plugins + components + integrations → modules)
  const modules = {
    ...(raw.plugins || {}),
    ...(raw.components || {}),
    ...(raw.integrations || {}),
    ...(raw.modules || {})
  };

  return Object.freeze({
      debug: Object.freeze(debugConfig),
      market: Object.freeze(validateMarket(raw.market)),
      discounts: Object.freeze(raw.discounts || {}),
      modules: Object.freeze(modules)
    });
}

/**
 * Ensures settings are loaded
 * @private
 */
function ensureSettings() {
  if (!settingsData) {
    settingsData = initSettings();
  }
  return settingsData;
}

/**
 * Framework Settings (lazy loaded, frozen)
 * 
 * @example
 * settings.debug.enabled // true
 * settings.market.timezone // 'Europe/Berlin'
 * settings.market.shipping.minimum // { amount: 395, currency_code: 'EUR' }
 * settings.market.stock.threshold_warning // 20
 * settings.market.stock.threshold_critical // 5
 * settings.discounts // { NEW10: { threshold: 3500, value: "10", type: "fixed", ends_at: null } }
 * settings.modules.accordion // { exclusive: true }
 */
export const settings = {
  /** Debug configuration */
  get debug() { return ensureSettings().debug; },
  
  /** Market configuration */
  get market() { return ensureSettings().market; },
  
  /** Discount MBW data from liftr_discount metaobjects */
  get discounts() { return ensureSettings().discounts; },
  
  /** Module configurations (flat) */
  get modules() { return ensureSettings().modules; }
};

// Export MARKET_DEFAULTS for logic.live.js
export { MARKET_DEFAULTS };